import { DASH } from './BillDetails/billDetailsUtils';

/** What the bill's summary says when its properties do not post to the same account. */
export const SEVERAL_GL_ACCOUNTS = 'Varies by property';

/** What it says when the account is genuinely unset, which is also what blocks the push. */
export const NO_GL_ACCOUNT = 'Not configured';

/** Within one property, what differs between lines is the utility each was configured under. */
export const SEVERAL_UTILITY_GL_ACCOUNTS = 'Varies by utility';

/**
 * A pushed bill whose accounts cannot be read back. Deliberately not "Not configured": the payable
 * posted somewhere, and saying it had no account would be a claim about the ledger that is false.
 */
export const NO_RECORDED_GL_ACCOUNT = 'Not recorded';

/**
 * An account as a person reads it in AppFolio's chart of accounts: the number first, because that is
 * what an accountant reconciles against, with the name to confirm it is the one they meant.
 */
export function formatGlAccount(account) {
  if (!account) return null;
  const label = [account.number, account.name].filter(Boolean).join(' · ');
  return label || null;
}

/**
 * What to show for one property's Accounts Payable account. Null while the reads are still out, so the
 * row stays empty rather than claiming a property is unconfigured on the way to finding out.
 */
export function getPropertyApGlLabel(_ref) {
  var _formatGlAccount;
  let account = _ref.account,
    hasRead = _ref.hasRead;
  if (!hasRead) return null;
  return (_formatGlAccount = formatGlAccount(account)) !== null && _formatGlAccount !== void 0 ? _formatGlAccount : NO_GL_ACCOUNT;
}

/**
 * The one account for the whole bill, when there is one.
 *
 * A bill can cover several properties and each posts to its own account, so a single line is only ever
 * true when they all agree. Where they do not it says so rather than naming one of them — presenting
 * one property's account as the bill's is the kind of wrong that gets reconciled against.
 */
export function getSummaryApGlLabel(_ref2) {
  let accounts = _ref2.accounts,
    hasRead = _ref2.hasRead;
  if (!hasRead) return DASH;
  if (!accounts.length) return DASH;
  const labels = new Set(accounts.map(account => {
    var _formatGlAccount2;
    return (_formatGlAccount2 = formatGlAccount(account)) !== null && _formatGlAccount2 !== void 0 ? _formatGlAccount2 : NO_GL_ACCOUNT;
  }));
  return labels.size === 1 ? [...labels][0] : SEVERAL_GL_ACCOUNTS;
}

/**
 * The recorded lines that posted for one property.
 *
 * Matched on the Livable property id the service resolved for each line. A line it could not resolve —
 * the mapping removed since, or the property mapped twice — belongs to no property here and is counted
 * only by the bill's summary, where it still registers as a difference rather than disappearing.
 */
function linesForProperty(lines, propertyLivableId) {
  if (!propertyLivableId) return [];
  const wanted = String(propertyLivableId).toLowerCase();
  return (lines || []).filter(line => {
    var _line$propertyLivable;
    return String((_line$propertyLivable = line === null || line === void 0 ? void 0 : line.propertyLivableId) !== null && _line$propertyLivable !== void 0 ? _line$propertyLivable : '').toLowerCase() === wanted;
  });
}

/** One label per distinct account across the given lines, with an unreadable account counted as its own. */
function recordedLabels(lines, getAccount) {
  return new Set((lines || []).map(line => {
    var _formatGlAccount3;
    return (_formatGlAccount3 = formatGlAccount(getAccount(line === null || line === void 0 ? void 0 : line.glAccountExternalId))) !== null && _formatGlAccount3 !== void 0 ? _formatGlAccount3 : NO_RECORDED_GL_ACCOUNT;
  }));
}

/**
 * What one property's line items say for a bill that has already been pushed: the account those lines
 * actually posted to, not the account the property is configured with today. The two differ the moment
 * someone re-points a mapping after a push, and the recorded one is the one that gets reconciled.
 */
export function getRecordedPropertyApGlLabel(_ref3) {
  let recorded = _ref3.recorded,
    propertyLivableId = _ref3.propertyLivableId,
    getAccount = _ref3.getAccount;
  if (!recorded) return null;
  if (!recorded.hasRecordedAccounts) return NO_RECORDED_GL_ACCOUNT;
  const labels = recordedLabels(linesForProperty(recorded.lines, propertyLivableId), getAccount);
  if (labels.size === 0) return NO_RECORDED_GL_ACCOUNT;
  return labels.size === 1 ? [...labels][0] : SEVERAL_UTILITY_GL_ACCOUNTS;
}

/**
 * The same for the bill as a whole, over every line the push sent rather than only the ones that
 * resolved to a property. A line whose property could not be resolved still posted to an account, so
 * dropping it here could turn a bill that posted to two accounts into one claiming a single account.
 */
export function getRecordedSummaryApGlLabel(_ref4) {
  let recorded = _ref4.recorded,
    getAccount = _ref4.getAccount;
  if (!recorded) return DASH;
  if (!recorded.hasRecordedAccounts) return NO_RECORDED_GL_ACCOUNT;
  const labels = recordedLabels(recorded.lines, getAccount);
  if (labels.size === 0) return NO_RECORDED_GL_ACCOUNT;
  return labels.size === 1 ? [...labels][0] : SEVERAL_GL_ACCOUNTS;
}